import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Brand } from "../components/Navbar";
import VideoHeroBg from "../components/service/VideoHeroBg";
import { apiError } from "../lib/api";

export function AuthShell({ children, title, subtitle }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-navy lg:block grain">
        <VideoHeroBg mp4="/videos/road.mp4" webm="/videos/road.webm"
                     poster="/videos/road_poster.jpg"
                     posterAlt="IndiaFast branded trucks on a highway" badge="Express Network" testid="auth-hero-video" />
        <div className="absolute -left-20 top-24 h-80 w-80 rounded-full bg-saffron/25 blur-3xl" />
        <div className="absolute bottom-10 right-0 h-72 w-72 rounded-full bg-express/25 blur-3xl" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Brand dark />
          <div>
            <h1 className="font-heading text-4xl font-extrabold leading-tight tracking-tight text-white">
              Ship Faster.<br />Track Smarter.<br /><span className="text-saffron">Deliver Worldwide.</span>
            </h1>
            <p className="mt-4 max-w-sm text-sm text-slate-400">
              29,000+ pincodes in India · 220+ countries · 17 courier partners · one INDIAFAST dashboard.
            </p>
          </div>
          <p className="text-xs text-slate-500">© 2026 INDIAFAST Global Couriers</p>
        </div>
      </div>
      <div className="flex items-center justify-center bg-slate-50 px-4 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden"><Brand /></div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl">{title}</h2>
          <p className="mt-1.5 text-sm text-slate-500">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm outline-none transition focus:border-saffron focus:ring-2 focus:ring-saffron/20";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await login(form.email, form.password, "web");
      const next = location.state?.next;
      if (next && !["crm", "pickup", "ccare"].includes(user.role)) navigate(next);
      else navigate(user.role === "crm" ? "/crm" : user.role === "pickup" ? "/pickup" : user.role === "ccare" ? "/ccare" : "/app");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Login to your INDIAFAST account">
      <form onSubmit={submit} className="space-y-4">
        {error && <p data-testid="login-error" className="rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</p>}
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
          <input type="email" required data-testid="login-email-input" className={inputCls}
                 value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Password</label>
          <input type="password" required data-testid="login-password-input" className={inputCls}
                 value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
        </div>
        <button type="submit" disabled={loading} data-testid="login-form-submit-button"
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-saffron py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 transition-colors hover:bg-saffron-hover disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Login
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        New to INDIAFAST? <Link to="/register" data-testid="login-register-link" className="font-bold text-saffron hover:underline">Create account</Link>
      </p>
    </AuthShell>
  );
}
